import { CalendarOutlined, EditOutlined, SearchOutlined } from "@ant-design/icons";
import {
    Button, Col, DatePicker, Divider, Form, InputNumber, Modal, Row,
    Select, Typography, Tag, Spin, List, Card as AntdCard, message
} from "antd";
import React, { useEffect, useState, useCallback } from "react";
import dayjs from "dayjs";
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

dayjs.extend(customParseFormat);

const { Text, Title: AntdTitle } = Typography;
const { Option } = Select;

const safeDayjs = (dateInput, format) => {
    if (!dateInput) return null;
    if (dayjs.isDayjs(dateInput) && dateInput.isValid()) return dateInput;
    const d = format ? dayjs(dateInput, format, true) : dayjs(dateInput);
    return d.isValid() ? d : null;
};

// Helper to get a value or a fallback if it's null/undefined/empty
const getSafeValue = (value, fallback = "N/A") => (value !== undefined && value !== null && value !== '') ? String(value) : fallback;

const ExamScheduleModal = ({
    visible,
    onClose,
    onSubmit,
    currentApplicant,
    form,
}) => {
    const [availableRooms, setAvailableRooms] = useState([]);
    const [availableDates, setAvailableDates] = useState([]);
    const [availableTimes, setAvailableTimes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAlternativeSlots, setShowAlternativeSlots] = useState(false);
    const [alternativeSlots, setAlternativeSlots] = useState([]);
    const [loadingAlternativeSlots, setLoadingAlternativeSlots] = useState(false);
    const [alternativeSlotDateRange, setAlternativeSlotDateRange] = useState(() => [dayjs(), dayjs().add(7, 'days')]);

    useEffect(() => {
        if (!visible || !currentApplicant) {
            return;
        }

        const initializeAndPrefill = async () => {
            setLoading(true);
            form.resetFields();
            setAvailableTimes([]);
            setShowAlternativeSlots(false);
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

            try {
                const [roomsRes, datesRes] = await Promise.all([
                    fetch(`${apiUrl}/api/admin/rooms`),
                    fetch(`${apiUrl}/api/admin/terms/available-dates`)
                ]);

                if (!roomsRes.ok) throw new Error('Failed to fetch rooms');
                const roomsResult = await roomsRes.json();
                if (roomsResult.success) setAvailableRooms(roomsResult.data);
                else throw new Error(roomsResult.message || 'Could not load rooms');

                if (!datesRes.ok) throw new Error('Failed to fetch available dates');
                const datesResult = await datesRes.json();
                const fetchedDates = datesResult.success ? datesResult.data : [];
                setAvailableDates(fetchedDates);
    
                let feeStatus = getSafeValue(currentApplicant.approvedExamFeeStatus, 'Required');
                if (!currentApplicant.approvedExamDate && !currentApplicant.approvedExamTimeSlotId) {
                    feeStatus = 'Required';
                }
                
                const finalFormValues = {
                    approvedFeeAmount: getSafeValue(currentApplicant.approvedExamFeeAmount, 500),
                    approvedExamFeeStatus: feeStatus,
                };

                const preferredSlotDetails = currentApplicant.selectedTimeSlotDetails;
                let targetSlotId = currentApplicant.approvedExamTimeSlotId || preferredSlotDetails?.slotId;
                let targetDate = currentApplicant.approvedExamDate || preferredSlotDetails?.date;
                const targetStartTime = preferredSlotDetails?.startTime;

                if (targetDate) {
                    const targetDateStr = dayjs(targetDate).format('YYYY-MM-DD');
                    if (fetchedDates.includes(targetDateStr)) {
                        finalFormValues.approvedDate = targetDateStr;
                        const timesResponse = await fetch(`${apiUrl}/api/admin/terms/available-slots?date=${targetDateStr}`);
                        if (!timesResponse.ok) throw new Error('Failed to fetch time slots for pre-fill.');
                        
                        const timesResult = await timesResponse.json();
                        const fetchedTimes = timesResult.success ? timesResult.data : [];
                        setAvailableTimes(fetchedTimes);
                        
                        let matchingSlot = targetSlotId ? fetchedTimes.find(slot => slot.slotId === targetSlotId) : null;
                        
                        if (!matchingSlot && targetStartTime) {
                            matchingSlot = fetchedTimes.find(slot => slot.startTime === targetStartTime);
                            if (matchingSlot) targetSlotId = matchingSlot.slotId;
                        }

                        if (matchingSlot) {
                            finalFormValues.approvedTimeSlotId = targetSlotId;
                            finalFormValues.approvedRoomId = matchingSlot.roomId;
                        } else if (preferredSlotDetails && !currentApplicant.approvedExamTimeSlotId) {
                            toast.warn("Applicant's specific preferred time is no longer available. Please select a new one.");
                        }
                    } else if (preferredSlotDetails && !currentApplicant.approvedExamTimeSlotId) {
                        toast.warn("Applicant's preferred date is no longer available. Please select a new slot.");
                    }
                }
                
                form.setFieldsValue(finalFormValues);

            } catch (error) {
                console.error("Error during modal initialization:", error);
                toast.error(`Error loading data: ${error.message}`);
            } finally {
                setLoading(false);
            }
        };

        initializeAndPrefill();
    }, [visible, currentApplicant, form]);
    
    const handleDateChange = async (selectedDate) => {
        form.setFieldsValue({ approvedTimeSlotId: null, approvedRoomId: null });
        if (!selectedDate) {
            setAvailableTimes([]);
            return;
        }
        setLoading(true);
        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
            const response = await fetch(`${apiUrl}/api/admin/terms/available-slots?date=${selectedDate}`);
            if (!response.ok) throw new Error('Failed to fetch time slots.');
            
            const result = await response.json();
            setAvailableTimes(result.success ? result.data : []);
            if (!result.success) toast.error(result.message || 'Could not load time slots.');
        } catch (error) {
            toast.error(error.message);
            setAvailableTimes([]);
        } finally {
            setLoading(false);
        }
    };

    const handleTimeChange = (slotId) => {
        const selectedSlot = availableTimes.find(slot => slot.slotId === slotId);
        if (selectedSlot) {
            form.setFieldsValue({ approvedRoomId: selectedSlot.roomId });
        }
    };

    const handleFormSubmit = async () => {
        try {
            const values = await form.validateFields();
            
            const selectedSlot = availableTimes.find(slot => slot.slotId === values.approvedTimeSlotId);
            if (!selectedSlot) {
                toast.error("The selected time slot could not be found. Please re-select the time.");
                return;
            }

            const processedValues = {
                ...values,
                approvedDate: dayjs(values.approvedDate),
                slotDetails: selectedSlot,
            };

            if (onSubmit) {
                onSubmit(processedValues);
            }
        } catch (errorInfo) {
            console.log('Validation Failed:', errorInfo);
            toast.error("Please fill out all required fields.");
        }
    };

    const renderStatusTag = (status) => {
        let color = "default";
        let displayStatus = status || 'N/A';
        switch (String(status).toLowerCase()) {
            case "complete": color = "success"; break;
            case "incomplete": color = "warning"; break;
            case "pending": color = "processing"; break;
            case "on-going": color = "blue"; break;
            case "required": color = "error"; break;
            case "paid": color = "success"; break;
            case "waived": color = "default"; break;
            default: color = "default";
        }
        return <Tag color={color}>{displayStatus}</Tag>;
    };

    const FEE_STATUS_OPTIONS = [ { value: "Required", label: "Required" }, { value: "Waived", label: "Waived" } ];

    const fetchAlternativeSlots = useCallback(async () => {
        if (!alternativeSlotDateRange || alternativeSlotDateRange.length !== 2) return;
        setLoadingAlternativeSlots(true);
        try {
            const startDate = alternativeSlotDateRange[0].format('YYYY-MM-DD');
            const endDate = alternativeSlotDateRange[1].format('YYYY-MM-DD');
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
            const response = await fetch(`${apiUrl}/api/admin/terms/available-slots?startDate=${startDate}&endDate=${endDate}`);
            if (!response.ok) throw new Error(`Failed to fetch alternative slots`);
            const result = await response.json();
            if (result.success) {
                setAlternativeSlots(result.data);
                if (result.data.length === 0) toast.info("No alternative slots found for this date range.");
            } else {
                toast.error(result.message);
                setAlternativeSlots([]);
            }
        } catch (error) {
            toast.error(`Error: ${error.message}`);
            setAlternativeSlots([]);
        } finally {
            setLoadingAlternativeSlots(false);
        }
    }, [alternativeSlotDateRange]);

    const handleSelectAlternativeSlot = async (slot) => {
        const dateString = dayjs(slot.date).format('YYYY-MM-DD');
        if (!availableDates.includes(dateString)) {
            setAvailableDates(prev => [...new Set([...prev, dateString])].sort());
        }
        // handleDateChange fetches times for the new date
        await handleDateChange(dateString);
        form.setFieldsValue({
            approvedDate: dateString,
            approvedTimeSlotId: slot.slotId,
            approvedRoomId: slot.roomId,
        });
        setShowAlternativeSlots(false);
        message.success(`Slot on ${dayjs(slot.date).format('MMM D')} at ${slot.startTime} has been pre-filled.`, 5);
    };

    return (
        <Modal
            title={<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><CalendarOutlined /><span>Manage Exam Schedule</span></div>}
            open={visible}
            onCancel={onClose}
            footer={[
                <Button key="cancel" onClick={onClose}>Cancel</Button>,
                <Button key="submit" type="primary" icon={<EditOutlined />} onClick={handleFormSubmit} loading={loading}>Update Schedule</Button>,
            ]}
            width={700}
            destroyOnClose
        >
            {loading ? <div style={{textAlign: 'center', padding: '50px'}}><Spin /></div> : 
            currentApplicant ? (
                <>
                    <AntdCard size="small" style={{ marginBottom: '1rem' }} variant="filled">
                        <AntdTitle level={5} style={{ marginTop: 0, marginBottom: 10 }}>Applicant Information</AntdTitle>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.5rem' }}>
                            <div><Text strong>Name:</Text> <Text>{`${getSafeValue(currentApplicant.lastName, '')}, ${getSafeValue(currentApplicant.firstName, '')} ${currentApplicant.middleName ? currentApplicant.middleName.charAt(0) + "." : ""}`.trim() || "N/A"}</Text></div>
                            <div><Text strong>Applicant ID:</Text> <Text>{getSafeValue(currentApplicant.applicantID)}</Text></div>
                            <div><Text strong>Email:</Text> <Text>{getSafeValue(currentApplicant.email)}</Text></div>
                            <div><Text strong>Mobile:</Text> <Text>{getSafeValue(currentApplicant.mobile)}</Text></div>
                            <div><Text strong>Academic:</Text> <Text>{`${getSafeValue(currentApplicant.academicLevel)} - ${getSafeValue(currentApplicant.academicStrand)}`}</Text></div>
                            <div><Text strong>Exam Details Status:</Text> {renderStatusTag(currentApplicant.admissionExamDetailsStatus)}</div>
                            <div style={{ gridColumn: 'span 2', marginTop: '8px' }}>
                                <Text strong>Applicant's Preferred Date:</Text>{" "}
                                <Text>{safeDayjs(currentApplicant.selectedTimeSlotDetails?.date)?.format('dddd, MMMM D, YYYY') || safeDayjs(currentApplicant.preferredExamAndInterviewDate)?.format('dddd, MMMM D, YYYY') || "N/A"}</Text>
                            </div>
                            {currentApplicant.selectedTimeSlotDetails && (
                                <div style={{ gridColumn: 'span 2' }}>
                                    <Text strong>Applicant's Preferred Slot:</Text>{" "}
                                    <Text>{currentApplicant.selectedTimeSlotDetails.startTime && currentApplicant.selectedTimeSlotDetails.endTime ? `From ${currentApplicant.selectedTimeSlotDetails.startTime} to ${currentApplicant.selectedTimeSlotDetails.endTime}` : "Time not specified"} {currentApplicant.selectedTimeSlotDetails.roomName && ` in ${currentApplicant.selectedTimeSlotDetails.roomName}`} {currentApplicant.selectedTimeSlotDetails.maxCapacity !== undefined && `(Slot Cap: ${currentApplicant.selectedTimeSlotDetails.maxCapacity})`}</Text>
                                </div>
                            )}
                        </div>
                    </AntdCard>

                    <Divider orientation="left">Set/Update Approved Schedule</Divider>
                    <Form form={form} layout="vertical" name="examScheduleForm">
                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item name="approvedDate" label="Approved Exam Date" rules={[{ required: true, message: "Please select an exam date" }]}>
                                    <Select placeholder="Select an available date" style={{ width: "100%" }} onChange={handleDateChange}>
                                        {availableDates.map(date => (<Option key={date} value={date}>{dayjs(date).format('dddd, MMMM D, YYYY')}</Option>))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item name="approvedTimeSlotId" label="Approved Exam Time" rules={[{ required: true, message: "Please select an exam time" }]}>
                                    <Select placeholder="Select a date first" style={{ width: "100%" }} disabled={!form.getFieldValue('approvedDate')} onChange={handleTimeChange}>
                                        {availableTimes.map(slot => (<Option key={slot.slotId} value={slot.slotId}>{`${slot.startTime} - ${slot.endTime} (Available Slots: ${slot.remainingCapacity})`}</Option>))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item name="approvedRoomId" label="Exam Room (Auto-assigned)">
                                    <Select placeholder="Auto-set by time" style={{ width: "100%" }} disabled>
                                        {availableRooms.map(room => (<Option key={room._id} value={room._id}>{room.name} (Capacity: {room.capacity})</Option>))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item name="approvedFeeAmount" label="Exam Fee Amount" rules={[{ required: true, message: "Please enter the exam fee" }]}>
                                    <InputNumber style={{ width: "100%" }} formatter={(value) => `₱ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")} parser={(value) => String(value).replace(/₱\s?|(,*)/g, "")} min={0} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item name="approvedExamFeeStatus" label="Exam Fee Status" rules={[{ required: true, message: "Please select the fee status" }]}>
                                    <Select placeholder="Select fee status" style={{ width: "100%" }}>
                                        {FEE_STATUS_OPTIONS.map(option => (<Option key={option.value} value={option.value}>{option.label}</Option>))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>

                    <Divider orientation="left" style={{ marginTop: '20px' }}>Assign to a Different Slot (Optional)</Divider>
                    <Button type="dashed" onClick={() => setShowAlternativeSlots(!showAlternativeSlots)} style={{ marginBottom: '16px', display: 'block', width: '100%' }}>
                        {showAlternativeSlots ? "Hide Alternative Slots Search" : "Find & Assign Alternative Slot"}
                    </Button>

                    {showAlternativeSlots && (
                        <AntdCard title="Search Available Alternative Slots" size="small" style={{ marginTop: '10px' }} variant="filled">
                            <Row gutter={[16, 16]} style={{ marginBottom: '12px' }}>
                                <Col xs={24} md={16}>
                                    <DatePicker.RangePicker value={alternativeSlotDateRange} onChange={(dates) => setAlternativeSlotDateRange(dates)} style={{ width: '100%' }} format="YYYY-MM-DD" />
                                </Col>
                                <Col xs={24} md={8}>
                                    <Button icon={<SearchOutlined />} onClick={fetchAlternativeSlots} loading={loadingAlternativeSlots} block type="primary">Search Slots</Button>
                                </Col>
                            </Row>
                            {loadingAlternativeSlots && <div style={{ textAlign: 'center', padding: '20px' }}><Spin /></div>}
                            {!loadingAlternativeSlots && alternativeSlots.length > 0 && (
                                <List
                                    itemLayout="horizontal"
                                    dataSource={alternativeSlots}
                                    size="small"
                                    bordered
                                    style={{ maxHeight: '250px', overflowY: 'auto', background: '#fff' }}
                                    renderItem={slot => (
                                        <List.Item actions={[<Button type="primary" ghost size="small" onClick={() => handleSelectAlternativeSlot(slot)}>Use this Slot</Button>]}>
                                            <List.Item.Meta
                                                title={<Text strong>{`${safeDayjs(slot.date)?.format('ddd, MMM D, YYYY')} @ ${slot.startTime}-${slot.endTime}`}</Text>}
                                                description={`Room: ${slot.roomName || 'N/A'} (Room Max: ${slot.roomPhysicalCapacity || 'N/A'}, Total Slots: ${slot.slotMaxCapacity}, Available: ${slot.remainingCapacity})`}
                                            />
                                        </List.Item>
                                    )}
                                />
                            )}
                            {!loadingAlternativeSlots && alternativeSlots.length === 0 && (
                                <div style={{ padding: '20px', textAlign: 'center' }}>
                                    <Text type="secondary">No slots found for the selected range.</Text>
                                </div>
                            )}
                        </AntdCard>
                    )}
                </>
            ) : (
                <Spin tip="Loading applicant details..." />
            )}
        </Modal>
    );
};

export default ExamScheduleModal;